import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// =============================================================================
// ANALYTICS CONFIGURATION
// =============================================================================

const LINKEDIN_PARTNER_ID = '8517018';
const META_PIXEL_ID = '1916199428986463';
const GA4_MEASUREMENT_ID = 'G-VV2F9PKVZQ';

// =============================================================================
// GOOGLE ANALYTICS 4
// =============================================================================

function initGA4() {
  if (typeof window === 'undefined' || !GA4_MEASUREMENT_ID) return;
  
  // Load gtag script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
  document.head.appendChild(script);
  
  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', GA4_MEASUREMENT_ID, {
    send_page_view: true
  });
}

export function trackGA4Event(eventName, params = {}) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, params);
  }
}

export function trackGA4PageView(path) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', GA4_MEASUREMENT_ID, {
      page_path: path
    });
  }
}

// =============================================================================
// INTERNAL ANALYTICS (Page Views to MongoDB)
// =============================================================================

async function trackPageViewInternal(pageData) {
  try {
    await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/analytics/pageview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(pageData),
    });
  } catch (error) {
    // Silently fail - don't break the site if analytics fails
    console.debug('Analytics tracking failed:', error);
  }
}

// =============================================================================
// UTM PARAMETER HANDLING
// =============================================================================

export function getUTMParams() {
  if (typeof window === 'undefined') return {};
  
  const params = new URLSearchParams(window.location.search);
  const utmParams = {
    utm_source: params.get('utm_source') || '',
    utm_medium: params.get('utm_medium') || '',
    utm_campaign: params.get('utm_campaign') || '',
    utm_content: params.get('utm_content') || '',
    utm_term: params.get('utm_term') || '',
  };
  
  // Store in sessionStorage for persistence across page navigations
  if (utmParams.utm_source) {
    sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
  }
  
  // Return stored params if current page doesn't have them
  const stored = sessionStorage.getItem('utm_params');
  if (stored && !utmParams.utm_source) {
    return JSON.parse(stored);
  }
  
  return utmParams;
}

// =============================================================================
// LINKEDIN INSIGHT TAG
// =============================================================================

function initLinkedIn() {
  if (typeof window === 'undefined') return;
  
  window._linkedin_partner_id = LINKEDIN_PARTNER_ID;
  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
  if (!window._linkedin_data_partner_ids.includes(LINKEDIN_PARTNER_ID)) {
    window._linkedin_data_partner_ids.push(LINKEDIN_PARTNER_ID);
  }
  
  // Load LinkedIn script if not already loaded
  if (!document.querySelector('script[src*="li.lms-analytics"]')) {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
    document.head.appendChild(script);
  }
}

export function trackLinkedInConversion(conversionId) {
  if (typeof window !== 'undefined' && window.lintrk) {
    window.lintrk('track', { conversion_id: conversionId });
  }
}

// =============================================================================
// META PIXEL
// =============================================================================

function initMetaPixel() {
  if (typeof window === 'undefined' || !META_PIXEL_ID) return;
  
  // Facebook Pixel base code
  !function(f,b,e,v,n,t,s)
  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
  n.queue=[];t=b.createElement(e);t.async=!0;
  t.src=v;s=b.getElementsByTagName(e)[0];
  s.parentNode.insertBefore(t,s)}(window, document,'script',
  'https://connect.facebook.net/en_US/fbevents.js');
  
  window.fbq('init', META_PIXEL_ID);
  window.fbq('track', 'PageView');
}

export function trackMetaEvent(eventName, params = {}) {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', eventName, params);
  }
}

// =============================================================================
// CUSTOM EVENTS
// =============================================================================

export function trackEvent(eventName, params = {}) {
  // Track on LinkedIn
  if (typeof window !== 'undefined' && window.lintrk) {
    window.lintrk('track', { conversion_id: eventName });
  }
  
  // Track on Meta
  trackMetaEvent(eventName, params);
  
  // Log for debugging
  if (process.env.NODE_ENV === 'development') {
    console.log('📊 Event tracked:', eventName, params);
  }
}

export function trackLead(leadData) {
  // LinkedIn Lead event
  if (typeof window !== 'undefined' && window.lintrk) {
    window.lintrk('track', { conversion_id: 'lead_form_submit' });
  }
  
  // Meta Lead event
  trackMetaEvent('Lead', {
    content_name: leadData.page_url || window.location.pathname,
    content_category: leadData.utm_campaign || 'organic',
  });
  
  // GA4 Lead event
  trackGA4Event('generate_lead', {
    currency: 'NOK',
    value: 1000,
    source: leadData.utm_source || 'direct'
  });
}

export function trackScrollDepth(percentage) {
  trackEvent(`scroll_${percentage}`);
  trackGA4Event('scroll', { percent_scrolled: percentage });
}

// =============================================================================
// ANALYTICS PROVIDER COMPONENT
// =============================================================================

export default function Analytics() {
  const location = useLocation();
  
  // Initialize tracking on mount
  useEffect(() => {
    initLinkedIn();
    initMetaPixel();
    initGA4();
  }, []);
  
  // Track page views on route change
  useEffect(() => {
    const utmParams = getUTMParams();
    
    // LinkedIn pageview
    if (typeof window !== 'undefined' && window.lintrk) {
      window.lintrk('track');
    }
    
    // Meta pageview
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', 'PageView');
    }
    
    // GA4 pageview
    trackGA4PageView(location.pathname);
    
    // Internal analytics (to MongoDB)
    trackPageViewInternal({
      path: location.pathname,
      referrer: document.referrer,
      utm_source: utmParams.utm_source || '',
      utm_medium: utmParams.utm_medium || '',
      utm_campaign: utmParams.utm_campaign || '',
      user_agent: navigator.userAgent,
      screen_width: window.innerWidth,
      timestamp: new Date().toISOString(),
    });
    
  }, [location.pathname]);
  
  // Scroll depth tracking
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const tracked = { 25: false, 50: false, 75: false, 100: false };
    
    const handleScroll = () => {
      const scrollPercent = Math.round(
        (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100
      );
      
      [25, 50, 75, 100].forEach(threshold => {
        if (scrollPercent >= threshold && !tracked[threshold]) {
          tracked[threshold] = true;
          trackScrollDepth(threshold);
        }
      });
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);
  
  return null; // This component doesn't render anything
}
